/**
 * Pure helpers for per-wallet (or per-inventory) tax strategy overrides on an
 * inventory view (BW4-3047). A view has one default picking strategy; these
 * overrides are sparse exceptions — "everything is FIFO except wallets A and B,
 * which are HIFO". Kept framework-free so it can be unit-tested directly.
 */

/**
 * Only the queue-based strategies can be overridden per wallet: they share one
 * executor and differ only in pop order. BalanceOnly, CostAverage, and
 * Specific Identification are separate executors and are intentionally not
 * offered here.
 */
export const QUEUE_BASED_TAX_STRATEGIES = ['FIFO', 'LIFO', 'HIFO'] as const;

export type QueueBasedTaxStrategy = typeof QUEUE_BASED_TAX_STRATEGIES[number];

export interface TaxStrategyOverrideRow {
  /** WalletId for per-wallet views; inventoryId for inventory-group views. */
  walletId: string;
  taxStrategy: QueueBasedTaxStrategy | '';
}

export function isQueueBasedStrategy(taxStrategy: string): taxStrategy is QueueBasedTaxStrategy {
  return (QUEUE_BASED_TAX_STRATEGIES as readonly string[]).includes(taxStrategy);
}

export function strategyOverrideOptions(enableTrueHifo: boolean): { label: string; value: QueueBasedTaxStrategy }[] {
  const options: { label: string; value: QueueBasedTaxStrategy }[] = [
    { label: 'First In, First Out (FIFO)', value: 'FIFO' },
    { label: 'Last In, First Out (LIFO)', value: 'LIFO' },
  ];
  if (enableTrueHifo) {
    options.push({ label: 'Highest In, First Out (HIFO)', value: 'HIFO' });
  }
  return options;
}

/**
 * Rows are valid when every entry is fully filled in and no wallet appears
 * twice (the API rejects duplicates).
 */
export function validateStrategyOverrideRows(rows: TaxStrategyOverrideRow[]): boolean {
  const seen = new Set<string>();
  for (const row of rows) {
    if (!row.walletId || !row.taxStrategy || !isQueueBasedStrategy(row.taxStrategy)) {
      return false;
    }
    if (seen.has(row.walletId)) {
      return false;
    }
    seen.add(row.walletId);
  }
  return true;
}

/**
 * Attaches the overrides to the create-view request. `strategyOverrides` is
 * not in the generated client yet (regenerate after the api-svc swagger
 * lands); until then this mirrors applyLabelRulesToConfig's single-cast
 * approach.
 */
export function applyStrategyOverridesToRequest<T extends object>(
  request: T,
  rows: TaxStrategyOverrideRow[]
): T & { strategyOverrides?: { walletId: string; taxStrategy: QueueBasedTaxStrategy }[] } {
  const withOverrides = request as T & {
    strategyOverrides?: { walletId: string; taxStrategy: QueueBasedTaxStrategy }[];
  };
  const complete = rows.filter(
    (row): row is TaxStrategyOverrideRow & { taxStrategy: QueueBasedTaxStrategy } =>
      !!row.walletId && isQueueBasedStrategy(row.taxStrategy)
  );
  if (complete.length > 0) {
    withOverrides.strategyOverrides = complete.map((row) => ({
      walletId: row.walletId,
      taxStrategy: row.taxStrategy,
    }));
  }
  return withOverrides;
}
